/**
 * B2 — Contabilidad: lo que se le pasa a la gestoría (libro de facturas expedidas, IVA
 * repercutido por trimestre) y lo que el despacho necesita para cobrar y para sus clientes
 * (cartera de cobros, provisiones y suplidos).
 *
 * Los emisores con régimen «Libros del autónomo» (persona física) suman, solo para ADMIN y
 * ABOGADO, sus facturas recibidas (gastos), los bienes de inversión y los borradores del 303 y
 * el 130. Cada sección tiene su propia ruta (`/accounting/<sección>`) y su entrada en el menú.
 */
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, BookOpen, X } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import ExpensesTab from '../components/accounting/ExpensesTab'
import InvestmentsTab from '../components/accounting/InvestmentsTab'
import TaxTab from '../components/accounting/TaxTab'
import FundsTab from '../components/accounting/FundsTab'
import LedgerTab from '../components/accounting/LedgerTab'
import ReceivablesTab from '../components/accounting/ReceivablesTab'
import VatTab from '../components/accounting/VatTab'
import { selectCls, useIssuersState, yearOptions } from '../components/accounting/common'
import { issuerHasBooks } from '../lib/ctb'

export const ACCOUNTING_SECTIONS = [
  { slug: 'ledger', label: 'Facturas expedidas', books: false },
  { slug: 'vat', label: 'IVA por trimestre', books: false },
  { slug: 'receivables', label: 'Cobros pendientes', books: false },
  { slug: 'funds', label: 'Provisiones y suplidos', books: false },
  { slug: 'expenses', label: 'Gastos', books: true },
  { slug: 'investments', label: 'Bienes de inversión', books: true },
  { slug: 'taxes', label: 'Resultado e impuestos', books: true },
] as const
type Slug = (typeof ACCOUNTING_SECTIONS)[number]['slug']

/** Los identificadores de las pestañas de antes de tener rutas propias (`/billing/books?tab=…`). */
const LEGACY_TAB: Record<string, Slug> = {
  libro: 'ledger',
  iva: 'vat',
  cobros: 'receivables',
  terceros: 'funds',
  gastos: 'expenses',
  inversiones: 'investments',
  impuestos: 'taxes',
}

/** `/billing/books` (enlaces antiguos) → `/accounting/<sección>` conservando el filtro de cliente. */
export function LegacyBooksRedirect() {
  const [params] = useSearchParams()
  const slug = LEGACY_TAB[params.get('tab') ?? ''] ?? 'ledger'
  const clientId = params.get('clientId')
  return <Navigate to={`/accounting/${slug}${clientId ? `?clientId=${clientId}` : ''}`} replace />
}

export default function AccountingPage() {
  const { tab: tabParam } = useParams<{ tab: string }>()
  const [params, setParams] = useSearchParams()
  const auth = useAuth()
  const { issuers, loaded } = useIssuersState()
  const canSeeBooks =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const bookIssuers = issuers.filter(issuerHasBooks)
  const booksAvailable = canSeeBooks && bookIssuers.length > 0
  const section = ACCOUNTING_SECTIONS.find((s) => s.slug === tabParam)
  const clientId = params.get('clientId') ?? ''
  const [issuerId, setIssuerId] = useState('')
  const [bookIssuerPick, setBookIssuerPick] = useState('')
  const [year, setYear] = useState(new Date().getFullYear())
  // Emisor con libros sobre el que trabajan las secciones de gastos e impuestos: el elegido arriba
  // si lleva libros; si no, el único que los lleva o el que se elija en la propia sección.
  const booksIssuerId =
    bookIssuers.find((i) => i.id === issuerId)?.id ??
    bookIssuers.find((i) => i.id === bookIssuerPick)?.id ??
    (bookIssuers.length === 1 ? bookIssuers[0]!.id : '')
  const [clientName, setClientName] = useState<string | null>(null)

  useEffect(() => {
    setClientName(null)
    if (!clientId) return
    api
      .get<{ client: { displayName: string } }>(`/clients/${clientId}`)
      .then((r) => setClientName(r.client.displayName))
      .catch(() => setClientName(null))
  }, [clientId])

  if (!section) return <Navigate to="/accounting/ledger" replace />

  const tabs = ACCOUNTING_SECTIONS.filter((s) => !s.books || booksAvailable)
  const clearClient = () => {
    const next = new URLSearchParams(params)
    next.delete('clientId')
    setParams(next, { replace: true })
  }
  const tab = section.slug
  const bookSection = section.books

  return (
    <div className="space-y-5 animate-slideUp">
      <header className="space-y-2">
        <Link
          to="/billing"
          className="t-muted text-sm hover:underline inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} /> Facturación
        </Link>
        <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
          <BookOpen size={20} className="text-toga-300" />
          Contabilidad · {section.label}
        </h1>
        <p className="t-muted text-sm max-w-3xl">
          Lo que se le pasa a la gestoría y lo que hace falta para cobrar. Toga no lleva la
          contabilidad por partida doble del despacho: estos informes salen de las facturas y son
          orientativos, no modelos oficiales.
          {booksAvailable && (
            <>
              {' '}
              Los emisores con libros del autónomo suman sus gastos, bienes de inversión y los
              borradores del 303 y el 130.
            </>
          )}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={issuerId}
          onChange={(e) => setIssuerId(e.target.value)}
          className={selectCls}
          aria-label="Emisor"
        >
          <option value="">Todos los emisores</option>
          {issuers.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
              {i.nif ? ` · ${i.nif}` : ''}
            </option>
          ))}
        </select>
        {(tab === 'ledger' || tab === 'vat' || tab === 'funds' || bookSection) && (
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className={selectCls}
            aria-label="Año"
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        )}
        {clientId && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-toga-500/40 bg-toga-500/10 t-primary">
            Cliente: {clientName ?? '…'}
            <button type="button" onClick={clearClient} aria-label="Quitar filtro de cliente">
              <X size={12} />
            </button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-base">
        {tabs.map((t) => (
          <Link
            key={t.slug}
            to={{
              pathname: `/accounting/${t.slug}`,
              search: clientId ? `?clientId=${clientId}` : '',
            }}
            replace
            className={[
              'px-3.5 py-2 text-sm -mb-px border-b-2',
              tab === t.slug
                ? 'border-toga-400 t-primary'
                : 'border-transparent t-muted hover:t-secondary',
            ].join(' ')}
          >
            {t.label}
          </Link>
        ))}
      </div>

      <section className="surface overflow-hidden">
        {tab === 'ledger' && <LedgerTab issuerId={issuerId} clientId={clientId} year={year} />}
        {tab === 'vat' && <VatTab issuerId={issuerId} year={year} />}
        {tab === 'receivables' && <ReceivablesTab issuerId={issuerId} clientId={clientId} />}
        {tab === 'funds' && <FundsTab issuerId={issuerId} year={year} />}

        {bookSection && loaded && !booksAvailable && (
          <div className="px-5 py-10 text-sm t-muted max-w-2xl space-y-2">
            <p className="t-primary font-medium">
              {canSeeBooks
                ? 'Ningún emisor lleva libros del autónomo.'
                : 'Esta sección es solo para administradores y abogados.'}
            </p>
            {canSeeBooks && (
              <p>
                Los gastos, los bienes de inversión y los borradores del 303 y el 130 son para un
                emisor persona física que lleve sus libros en Toga.{' '}
                {isAdmin ? (
                  <>
                    Actívalo en{' '}
                    <Link to="/settings/billing" className="text-toga-300 hover:underline">
                      Ajustes → Facturación → Emisores
                    </Link>{' '}
                    («Contabilidad de este emisor»).
                  </>
                ) : (
                  'Pide a un administrador que lo active en Ajustes → Facturación → Emisores.'
                )}
              </p>
            )}
          </div>
        )}
        {bookSection && booksAvailable && !booksIssuerId && (
          <div className="px-5 py-8 text-sm t-muted flex flex-wrap items-center gap-3">
            Elige el emisor con libros del autónomo:
            <select
              value={bookIssuerPick}
              onChange={(e) => setBookIssuerPick(e.target.value)}
              className={selectCls}
              aria-label="Emisor con libros"
            >
              <option value="">—</option>
              {bookIssuers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} · {i.nif}
                </option>
              ))}
            </select>
          </div>
        )}
        {tab === 'expenses' && booksAvailable && booksIssuerId && (
          <ExpensesTab issuerId={booksIssuerId} year={year} />
        )}
        {tab === 'investments' && booksAvailable && booksIssuerId && (
          <InvestmentsTab issuerId={booksIssuerId} year={year} />
        )}
        {tab === 'taxes' && booksAvailable && booksIssuerId && (
          <TaxTab issuerId={booksIssuerId} year={year} />
        )}
      </section>
    </div>
  )
}
